import type {DeckLocation} from './model';

type PresenterMessage =
  | {type: 'location'; location: DeckLocation}
  | {type: 'blackout'; value: boolean}
  | {type: 'request-blackout'}
  | {type: 'command'; command: 'forward' | 'backward' | 'blackout'};

export class PresenterBridge {
  readonly channel: BroadcastChannel;
  #blackout?: boolean;

  constructor(deckId: string) {
    this.channel = new BroadcastChannel(`slides2:${deckId}`);
  }

  sendLocation(location: DeckLocation): void {
    this.channel.postMessage({type: 'location', location} satisfies PresenterMessage);
  }

  sendCommand(command: 'forward' | 'backward' | 'blackout'): void {
    this.channel.postMessage({type: 'command', command} satisfies PresenterMessage);
  }

  sendBlackout(value: boolean): void {
    this.#blackout = value;
    this.channel.postMessage({type: 'blackout', value} satisfies PresenterMessage);
  }

  /** A late-opened window asks; a fresh default must never overwrite live state. */
  requestBlackout(): void {
    this.channel.postMessage({type: 'request-blackout'} satisfies PresenterMessage);
  }

  listen(handler: (message: PresenterMessage) => void): () => void {
    const listener = (event: MessageEvent<PresenterMessage>) => {
      const message = event.data;
      if (message.type === 'request-blackout') {
        if (this.#blackout !== undefined) this.sendBlackout(this.#blackout);
        return;
      }
      if (message.type === 'blackout') this.#blackout = message.value;
      handler(message);
    };
    this.channel.addEventListener('message', listener);
    return () => this.channel.removeEventListener('message', listener);
  }

  close(): void {
    this.channel.close();
  }
}
